<template>
  <v-container class="pa-0" :fill-height="!isFetched">
    <template v-if="isFetched">
      <v-row no-gutters v-if="!hideTitleRow">
        <v-col>
          <card-title-row
            :translation-path="titleTranslationPath"
            :straight-title="straightTitle"
            v-bind="$props"
            :class="hideTitlePadding ? 'pb-0' : 'pb-7'"
            :custom-size="titleSize"
            :has-icon-column="false"
            :title-classes="$attrs.titleClasses"
            :has-buttons="false"
          >
            <template #subtitle>
              <italic-subheader
                :translation-path="subtitlePath"
                :params="subtitleParams"
                style="font-size: 14px !important;"
                class="text-left"
              ></italic-subheader>
            </template>
          </card-title-row>
        </v-col>
      </v-row>
      <slot name="top"> </slot>
      <v-row no-gutters :class="{ 'absolute-buttons-margin': useStickyButtons }">
        <v-col :cols="12" class="pb-5">
          <slot name="form">
            <admin-input-row
              v-if="moduleEnabler"
              v-bind="{ ...moduleEnabler, ...moduleEnablerInput }"
              class="py-3"
              @change="changeModuleEnabler"
            />
            <template v-if="isModuleVisible && isFormFetched">
              <admin-form
                ref="form"
                :inputs="inputs"
                @update:isFormValid="isFormValid = $event"
                v-on="$listeners"
                :disabled="disabled"
              >
                <template v-for="(value, key) in $scopedSlots" #[key]="scope">
                  <slot :name="key" v-bind="scope" />
                </template>
              </admin-form>
              <slot name="postForm"> </slot>
            </template>
            <template v-else>
              <main-loader v-if="!isFormFetched"> </main-loader>
            </template>
          </slot>
        </v-col>
      </v-row>
      <v-row
        v-if="hasButtons && isModuleVisible && isFormFetched"
        no-gutters
        class="justify-start align-center"
        :class="{ 'absolute-buttons': useStickyButtons }"
      >
        <v-col cols="auto">
          <base-button
            style="padding-right: 24px; padding-left: 24px"
            translation-path="button.save"
            @click="save"
            :disabled="isDisabled"
            :loading="computedIsSaving"
          ></base-button>
        </v-col>
        <slot name="additional-buttons" />
      </v-row>
    </template>
    <template v-else>
      <main-loader> </main-loader>
    </template>
  </v-container>
</template>

<script>
import { call, get, sync } from 'vuex-pathify';
import CardTitleRow from '@/components/CardTitleRow';
import ItalicSubheader from '@/components/ItalicSubheader';
import AdminForm from '@/components/AdminForm';
import AdminInputRow from '@/components/AdminInputRow';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'AdminWrapper',
  components: {
    CardTitleRow,
    ItalicSubheader,
    AdminForm,
    AdminInputRow,
    MainLoader,
  },
  props: {
    hasPostForm: {
      type: Boolean,
      default: false,
    },
    hasButtons: {
      type: Boolean,
      default: true,
    },
    isSubtitle: {
      type: Boolean,
      default: false,
    },
    titleTranslationPath: {
      type: String,
      default: '',
    },
    isPlural: {
      type: Boolean,
      default: false,
    },
    amount: {
      type: Number,
      default: 0,
    },
    /**    titleTranslationPath: {

     * Title that should not be translated, e.g layer card title.
     */
    straightTitle: {
      type: String,
      default: '',
    },
    subtitlePath: {
      type: String,
      default: '',
    },
    subtitleParams: {
      type: Object,
      default: () => {},
    },
    inputs: {
      type: Array,
      default: () => [],
    },
    // isFormValid: {
    //   type: Boolean,
    //   default: true,
    // },
    isFetched: {
      type: Boolean,
      default: true,
    },
    isFormFetched: {
      type: Boolean,
      default: true,
    },
    isSuperAdmin: {
      type: Boolean,
      default: false,
    },
    hasValidation: {
      type: Boolean,
      default: true,
    },
    titleSize: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isCustomSaving: {
      type: Boolean,
      default: false,
    },
    moduleEnabler: {
      type: Object,
    },
    hideTitleRow: {
      type: Boolean,
      default: false,
    },
    saveCustomValidation: {
      type: Boolean,
      default: true,
    },
    hideTitlePadding: {
      type: Boolean,
      default: false,
    },
    disableStickyButtons: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isFormValid: false,
    isSaving: false,
    moduleEnablerInput: {
      canBeDisabled: false,
      hasSuffix: false,
      dataType: {
        name: 'switch',
      },
    },
  }),
  computed: {
    isAdminContentOverflowing: sync('tools/isAdminContentOverflowing'),
    useStickyButtons() {
      return !this.disableStickyButtons && this.isAdminContentOverflowing;
    },
    isModuleEnabled: get('admin/settingsValues@:moduleName'),
    // computedIsFormValid: {
    //   get() {
    //     return this.isFormValid;
    //   },
    //   set(nV) {
    //     this.$emit('update:isFormValid', nV);
    //   },
    // },
    computedIsSaving() {
      return this.isCustomSaving ? this.isCustomSaving : this.isSaving;
    },
    isDisabled() {
      return this.hasValidation && (this.disabled || !this.isFormValid || !this.saveCustomValidation);
    },
    isModuleVisible() {
      return !this.moduleEnabler || this.isModuleEnabled;
    },
    moduleName() {
      return this.moduleEnabler.name;
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    changeModuleEnabler(nV) {
      this.editSingleSetting({
        key: this.moduleName,
        body: {
          value: nV,
        },
      });
    },
    save() {
      this.$refs?.form?.save();
      this.$emit('save');
    },
  },
  watch: {
    isFormValid(nV) {
      this.$emit('validate', nV);
    },
  },
  mounted() {
    this.$root.$on('adminSavingON', () => (this.isSaving = true));
    this.$root.$on('adminSavingOFF', () => (this.isSaving = false));
  },
};
</script>

<style lang="scss" scoped>
.absolute-buttons {
  background-color: white;
  z-index: 2;
  box-shadow: 0px -3px 6px rgba(0, 0, 0, 0.15);

  width: calc(100% - 250px);
  height: 55px;
  position: fixed;
  bottom: 0;
  left: 250px;
  padding-left: 60px;
}
.absolute-buttons-margin {
  margin-bottom: 32px;
}
</style>
